import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ContractDisplayItem } from '@/components/ui/contract-display-item';
import { SearchInput } from '@/components/ui/search-input';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';

import {
	CONTRACTS_PANEL_BY_CUSTOMER_QUERY,
	CONTRACTS_PANEL_BY_VEHICLE_QUERY
} from './contracts.gql';

const CONTRACTS_PANEL_PAGE_SIZE = 100;

interface ContractPanelListItem {
	contractId: string;
	contractNumber?: string | null;
	beginDate?: string | null;
	endDate?: string | null;
	amount?: number | null;
	simultaneousVehicleLimit?: number | null;
	isActive: boolean;
}

interface ContractsPanelByCustomerQueryResponse {
	contracts?: {
		nodes?: ContractPanelListItem[] | null;
	} | null;
}

interface ContractsPanelByVehicleQueryResponse {
	vehicle?: {
		contractVehicles?: Array<{
			contractVehicleId: string;
			contract?: ContractPanelListItem | null;
		}> | null;
	} | null;
}

interface ContractsListPanelProps {
	customerId?: string;
	vehicleId?: string;
	isActive?: boolean;
	emptyMessage?: string;
	className?: string;
}

type StatusFilterValue = 'all' | 'active' | 'inactive';

export function ContractsListPanel({
	customerId,
	vehicleId,
	isActive = true,
	emptyMessage,
	className
}: ContractsListPanelProps) {
	const { t } = useTranslation('common');
	const { t: tContracts } = useTranslation('contracts');
	const [searchText, setSearchText] = useState('');
	const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');

	const {
		data: customerData,
		loading: customerLoading,
		error: customerError,
		refetch: refetchCustomer
	} = useQuery<ContractsPanelByCustomerQueryResponse>(
		CONTRACTS_PANEL_BY_CUSTOMER_QUERY,
		{
			variables: {
				customerId,
				first: CONTRACTS_PANEL_PAGE_SIZE
			},
			skip: !isActive || !customerId,
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		}
	);

	const {
		data: vehicleData,
		loading: vehicleLoading,
		error: vehicleError,
		refetch: refetchVehicle
	} = useQuery<ContractsPanelByVehicleQueryResponse>(
		CONTRACTS_PANEL_BY_VEHICLE_QUERY,
		{
			variables: {
				vehicleId
			},
			skip: !isActive || !vehicleId,
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		}
	);

	const isCustomerMode = Boolean(customerId);
	const loading = isCustomerMode ? customerLoading : vehicleLoading;
	const error = isCustomerMode ? customerError : vehicleError;

	const items = useMemo(() => {
		if (customerId) {
			return customerData?.contracts?.nodes ?? [];
		}

		if (vehicleId) {
			const mappedContracts = (vehicleData?.vehicle?.contractVehicles ?? [])
				.map(item => item?.contract)
				.filter(
					(contract): contract is ContractPanelListItem => Boolean(contract)
				);

			return Array.from(
				new Map(mappedContracts.map(contract => [contract.contractId, contract]))
					.values()
			);
		}

		return [];
	}, [
		customerData?.contracts?.nodes,
		customerId,
		vehicleData?.vehicle?.contractVehicles,
		vehicleId
	]);

	const filteredItems = useMemo(() => {
		const normalizedSearch = searchText.trim().toLowerCase();

		return items.filter(item => {
			if (
				statusFilter === 'active' &&
				!item.isActive
			) {
				return false;
			}

			if (
				statusFilter === 'inactive' &&
				item.isActive
			) {
				return false;
			}

			if (!normalizedSearch) {
				return true;
			}

			return item.contractId.toLowerCase().includes(normalizedSearch);
		});
	}, [items, searchText, statusFilter]);

	const hasActiveFilters =
		searchText.trim().length > 0 || statusFilter !== 'all';

	if (!isActive) {
		return null;
	}

	return (
		<div className={className}>
			<div className="mb-3 rounded-lg bg-gray-50 px-3 py-3 dark:bg-slate-800/50">
				<div className="flex flex-wrap gap-2">
					<div className="w-44">
						<SearchInput
							value={searchText}
							onChange={event => setSearchText(event.target.value)}
							placeholder={tContracts('list.searchPlaceholder')}
							variant="transparent"
							className="h-8 text-xs"
						/>
					</div>
					<div className="w-36">
						<SentryDropdown
							options={[
								{
									value: 'all',
									label: t('placeholders.all_statuses')
								},
								{
									value: 'active',
									label: t('status.active')
								},
								{
									value: 'inactive',
									label: t('status.inactive')
								}
							]}
							value={statusFilter}
							defaultValue="all"
							onChange={value =>
								setStatusFilter(value as StatusFilterValue)
							}
							placeholder={t('placeholders.all_statuses')}
							groupByCategory={false}
							className="h-8 w-full text-xs"
							variant="transparent"
						/>
					</div>
					{hasActiveFilters && (
						<Button
							variant="outline"
							size="sm"
							className="h-8 text-xs"
							onClick={() => {
								setSearchText('');
								setStatusFilter('all');
							}}
						>
							{t('buttons.reset')}
						</Button>
					)}
				</div>
			</div>

			{error ? (
				<div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
					<span>{error.message || t('messages.error')}</span>
					<Button
						size="sm"
						variant="outline"
						onClick={() => {
							if (customerId) {
								void refetchCustomer({
									customerId,
									first: CONTRACTS_PANEL_PAGE_SIZE
								});
								return;
							}

							if (vehicleId) {
								void refetchVehicle({ vehicleId });
							}
						}}
					>
						{t('buttons.refresh')}
					</Button>
				</div>
			) : null}

			{!error && loading ? (
				<div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
					<Loader2 className="h-4 w-4 animate-spin" />
					<span>{t('messages.loading')}</span>
				</div>
			) : null}

			{!error && !loading && filteredItems.length === 0 ? (
				<div className="py-6 text-sm text-muted-foreground">
					{emptyMessage ?? t('messages.no_data')}
				</div>
			) : (
				<div className="divide-y divide-border text-sm">
					{filteredItems.map(item => (
						<div key={item.contractId} className="py-3 first:pt-0 last:pb-0">
							<ContractDisplayItem
								contractId={item.contractId}
								contractNumber={item.contractNumber}
								beginDate={item.beginDate}
								endDate={item.endDate}
								amount={item.amount}
								simultaneousVehicleLimit={item.simultaneousVehicleLimit}
								isActive={item.isActive}
								inactiveLabel={t('status.inactive')}
							/>
						</div>
					))}
				</div>
			)}

			<div className="mt-3 flex items-center justify-end border-t border-gray-200 pt-2 dark:border-slate-700">
				<span className="text-sm text-muted-foreground">
					{t('pagination.viewingCount', {
						count: filteredItems.length,
						entityName: tContracts('entityName')
					})}
				</span>
			</div>
		</div>
	);
}
